/**
 * Modal d'avertissement d'expiration de session
 * Design System V2 - Teal/Purple gradient
 *
 * Note: Avec le nouveau système d'authentification (refresh token via cookies HttpOnly),
 * cette modal ne devrait apparaître que si le refresh automatique échoue.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, LogOut, RefreshCw } from 'lucide-react';

interface SessionWarningModalProps {
  isOpen: boolean;
  timeUntilExpiry: number; // En secondes
  onStayConnected: () => void;
  onLogout: () => void;
  onDismiss: () => void;
}

export default function SessionWarningModal({
  isOpen,
  timeUntilExpiry,
  onStayConnected,
  onLogout,
  onDismiss
}: SessionWarningModalProps) {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(timeUntilExpiry);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    setCountdown(timeUntilExpiry);
  }, [timeUntilExpiry]);

  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          onLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, onLogout]);

  const handleStayConnected = async () => {
    setIsRefreshing(true);
    try {
      await onStayConnected();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!isOpen) return null;

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* En-tête - Design V2 */}
        <div className="bg-gradient-to-r from-teal-600 to-purple-600 p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 p-3 rounded-xl">
              <Clock className="w-7 h-7" />
            </div>
            <div>
              <h3 className="text-xl font-bold">{t('auth.session.warningTitle')}</h3>
              <p className="text-sm text-white/80 mt-1">
                {t('auth.session.warningSubtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Contenu */}
        <div className="p-6 space-y-5">
          {/* Compte à rebours */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 text-center border border-gray-200">
            <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              {t('auth.session.timeRemaining')}
            </span>
            <div className="text-5xl font-bold text-teal-600 font-mono mt-2">
              {minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}
            </div>
          </div>

          {/* Message */}
          <p className="text-sm text-gray-600 text-center">
            {t('auth.session.securityMessage')}
          </p>
        </div>

        {/* Actions */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-100">
          <button
            onClick={onLogout}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            <span>{t('auth.session.logout')}</span>
          </button>
          <button
            onClick={handleStayConnected}
            disabled={isRefreshing}
            className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 rounded-lg shadow-md hover:shadow-lg transition-all flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>{isRefreshing ? t('auth.session.connecting') : t('auth.session.stayConnected')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
